import { useState } from 'react';
import { mutate } from 'swr';

import useToast from '@/components/Toast/useToast';
import {
  matchKeyPartial,
  useBulkExampleCreate,
  useBulkExampleUpdate,
  useOrganizationId,
} from '@/hooks/useSwr';
import { apiExamplesPath } from '@/utils/constants';
import { xCount } from '@/utils/stringUtils';

import { ExampleSchemaOrDraftWithEdited } from '../../PlaygroundContext';

export const useUpdateAndCreateDatasetExamples = ({
  datasetId,
  onSuccess,
}: {
  datasetId: string;
  onSuccess?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const organizationId = useOrganizationId();
  const { createToast } = useToast();
  const datasetDataType = 'kv';
  const { trigger: addExamples } = useBulkExampleCreate({ datasetDataType });
  const { trigger: updateExamples } = useBulkExampleUpdate({});

  const handleApiSuccess = (newCount: number, updatedCount: number) => {
    let description = '';

    if (updatedCount > 0 && newCount > 0) {
      description = `${xCount('example', updatedCount)} updated, ${xCount(
        'example',
        newCount
      )} created`;
    } else if (newCount > 0) {
      description = `Added ${xCount('example', newCount)}`;
    } else if (updatedCount > 0) {
      description = `${xCount('example', updatedCount)} updated`;
    }

    createToast({
      title: 'Dataset updated',
      description,
    });

    // Refresh related data
    mutate(matchKeyPartial({ url: `${apiExamplesPath}` }));
    mutate(matchKeyPartial({ url: `${apiExamplesPath}/count` }));
    onSuccess?.();
  };

  const handleApiError = (
    error: unknown,
    hasExisting: boolean,
    hasNew: boolean
  ) => {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error occurred';

    let title = 'Operation failed';
    if (hasExisting && hasNew) {
      title = 'Failed to update and add examples';
    } else if (hasExisting) {
      title = 'Failed to update examples';
    } else if (hasNew) {
      title = 'Failed to add examples';
    }

    createToast({
      title,
      description: errorMessage,
    });
  };

  const addNewExamples = (newExamples: ExampleSchemaOrDraftWithEdited[]) => {
    return addExamples({
      dataset_id: datasetId,
      examples: newExamples.map((example) => ({
        inputs: JSON.stringify(example.inputs),
        outputs: JSON.stringify(example.outputs),
        metadata: JSON.stringify(example.metadata),
      })),
    });
  };

  const updateExistingExamples = (
    editedExistingExamples: ExampleSchemaOrDraftWithEdited[]
  ) => {
    return updateExamples({
      json: editedExistingExamples.map((example) => ({
        id: example.id,
        inputs: example.inputs,
        outputs: example.outputs,
        metadata: example.metadata,
      })),
    });
  };

  const updateDataset = async (
    newExamples: ExampleSchemaOrDraftWithEdited[],
    editedExistingExamples: ExampleSchemaOrDraftWithEdited[]
  ) => {
    const hasNew = newExamples.length > 0;
    const hasExisting = editedExistingExamples.length > 0;

    if (!organizationId) {
      createToast({
        title: 'Error',
        description: 'Organization not found',
      });
      return;
    }

    if (!hasNew && !hasExisting) {
      // Nothing to do
      onSuccess?.();
      return;
    }

    try {
      setIsLoading(true);

      if (hasNew && hasExisting) {
        await Promise.all([
          addNewExamples(newExamples),
          updateExistingExamples(editedExistingExamples),
        ]);
      } else if (hasNew) {
        await addNewExamples(newExamples);
      } else if (hasExisting) {
        await updateExistingExamples(editedExistingExamples);
      }

      handleApiSuccess(newExamples.length, editedExistingExamples.length);
    } catch (error) {
      handleApiError(error, hasExisting, hasNew);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    updateDataset,
  };
};
